import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { IoArrowBackOutline } from 'react-icons/io5';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { CreateBatchRequest } from 'services/interfaces/nfse';
import * as nfseService from 'services/nfse';
import CsvPreview from './components/CsvPreview';
import CsvUploader from './components/CsvUploader';
import { formatCurrency, formatDateISO } from './helpers/csvValidation';
import {
  BatchFormData,
  CsvRow,
  CsvValidationError,
  NATIONAL_TAX_CODE_OPTIONS,
  NBS_CODE_WITH_COMPLEMENT,
  SERVICE_COMPLEMENT_OPTIONS,
  SERVICE_NBS_OPTIONS,
} from './types/nfse.types';

const NfseNovoLote = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');

  const [formData, setFormData] = useState<BatchFormData>({
    referenceDate: formatDateISO(new Date()),
    nationalTaxCode: '100201',
    serviceDescription:
      'Taxa de intermediação de pedidos realizados por meio da plataforma Yooga',
    serviceNbs: '109059000',
    hasMunicipalBenefit: false,
    serviceComplement: '',
  });

  const showComplementSelect =
    formData.nationalTaxCode === NBS_CODE_WITH_COMPLEMENT;

  const [csvData, setCsvData] = useState<CsvRow[]>([]);
  const [csvErrors, setCsvErrors] = useState<CsvValidationError[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [invalidDocuments, setInvalidDocuments] = useState<Set<string>>(
    new Set(),
  );
  const [isValidated, setIsValidated] = useState(false);

  const getDescriptionFromNbs = (nbsValue: string): string => {
    const option = SERVICE_NBS_OPTIONS.find((opt) => opt.value === nbsValue);
    if (!option) return '';
    const parts = option.label.split(' - ');
    return parts.length > 1 ? parts.slice(1).join(' - ') : option.label;
  };

  const handleFormChange = (
    field: keyof BatchFormData,
    value: string | boolean,
  ) => {
    if (field === 'serviceNbs' && typeof value === 'string') {
      setFormData((prev) => ({
        ...prev,
        serviceNbs: value,
        serviceDescription: getDescriptionFromNbs(value),
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCsvValidated = useCallback(
    (data: CsvRow[], errors: CsvValidationError[]) => {
      setCsvData(data);
      setCsvErrors(errors);
      setValidationErrors([]);
      setInvalidDocuments(new Set());
      setIsValidated(false);
      if (data.length > 0 && errors.length === 0) {
        setShowPreview(true);
      }
    },
    [],
  );

  const handleCsvClear = useCallback(() => {
    setCsvData([]);
    setCsvErrors([]);
    setShowPreview(false);
    setValidationErrors([]);
    setInvalidDocuments(new Set());
    setIsValidated(false);
  }, []);

  const summary = useMemo(() => {
    const total = csvData.reduce(
      (sum, row) =>
        sum +
        (typeof row.value === 'number'
          ? row.value
          : parseFloat(String(row.value)) || 0),
      0,
    );
    return {
      quantity: csvData.length,
      totalValue: total,
    };
  }, [csvData]);

  const handleValidate = async () => {
    if (csvData.length === 0) {
      toast({
        title: 'Atenção',
        description: 'Importe um arquivo CSV antes de continuar',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsValidating(true);
    setValidationErrors([]);
    setInvalidDocuments(new Set());

    try {
      const response = await nfseService.validateBatch({
        items: csvData.map((row) => ({
          document: String(row.document),
          value:
            typeof row.value === 'number'
              ? row.value
              : parseFloat(String(row.value)) || 0,
        })),
      });

      const validatedItems = response.data;
      const itemsWithoutCustomer = validatedItems.filter(
        (item) => item.customerId === null,
      );

      if (itemsWithoutCustomer.length > 0) {
        const errors = itemsWithoutCustomer.map(
          (item) => `Documento ${item.document}: ${item.customerName}`,
        );
        const invalidDocs = new Set(
          itemsWithoutCustomer.map((item) => item.document),
        );
        setValidationErrors(errors);
        setInvalidDocuments(invalidDocs);
        toast({
          title: 'Validação com avisos',
          description: `${errors.length} item(s) sem cliente identificado`,
          status: 'warning',
          duration: 5000,
        });
      }

      setIsValidated(true);
      toast({
        title: 'Validação concluída',
        description: `${validatedItems.length} item(s) validado(s)`,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description:
          error instanceof Error ? error.message : 'Erro ao validar lote',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async () => {
    if (csvData.length === 0) {
      toast({
        title: 'Atenção',
        description: 'Importe um arquivo CSV antes de continuar',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const fullDescription = formData.serviceComplement
        ? `${formData.serviceDescription} ${formData.serviceComplement}`
        : formData.serviceDescription;

      const payload: CreateBatchRequest = {
        items: csvData.map((row) => ({
          document: String(row.document),
          value:
            typeof row.value === 'number'
              ? row.value
              : parseFloat(String(row.value)) || 0,
        })),
        referenceDate: formData.referenceDate,
        nationalTaxCode: formData.nationalTaxCode,
        serviceDescription: fullDescription,
        serviceNbs: formData.serviceNbs,
        hasMunicipalBenefit: formData.hasMunicipalBenefit,
      };

      const response = await nfseService.createBatch(payload);

      toast({
        title: 'Sucesso',
        description: 'Lote criado com sucesso!',
        status: 'success',
        duration: 3000,
      });

      navigate(`/financeiro/lote/${response.data.id}`);
    } catch (error) {
      toast({
        title: 'Erro',
        description:
          error instanceof Error ? error.message : 'Erro ao criar lote',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack
      minW={{ lg: '740px' }}
      w={{ base: '90%', lg: '85%' }}
      m="auto"
      p={{ base: '2rem 1rem', lg: '4rem' }}
      gap="1.5rem"
    >
      <HStack spacing="0.5rem">
        <IconButton
          size="sm"
          variant="ghost"
          icon={<IoArrowBackOutline />}
          onClick={() => navigate('/financeiro')}
          aria-label="Voltar"
        />
        <Breadcrumb>
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to="/financeiro">
              NFS-e
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>Novo lote</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
      </HStack>

      <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
        <Stack gap="1.5rem">
          {/* Form Header */}
          <Grid templateColumns="repeat(12, 1fr)" gap="1rem" alignItems="end">
            <GridItem colSpan={{ base: 12, md: 3 }}>
              <FormControl>
                <FormLabel fontSize="sm">Data de Referência</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  value={formData.referenceDate}
                  onChange={(e) =>
                    handleFormChange('referenceDate', e.target.value)
                  }
                />
              </FormControl>
            </GridItem>

            <GridItem colSpan={{ base: 12, md: 3 }}>
              <FormControl>
                <FormLabel fontSize="sm">Código NBS</FormLabel>
                <Select
                  size="sm"
                  value={formData.nationalTaxCode}
                  onChange={(e) =>
                    handleFormChange('nationalTaxCode', e.target.value)
                  }
                >
                  {NATIONAL_TAX_CODE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            <GridItem colSpan={{ base: 12, md: 3 }}>
              <FormControl>
                <FormLabel fontSize="sm">Descrição NBS</FormLabel>
                <Select
                  size="sm"
                  value={formData.serviceNbs}
                  onChange={(e) =>
                    handleFormChange('serviceNbs', e.target.value)
                  }
                >
                  {SERVICE_NBS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            <GridItem colSpan={{ base: 12, md: 3 }}>
              <FormControl>
                <HStack spacing="0.25rem">
                  <FormLabel fontSize="sm" mb="0">
                    Benefício Municipal
                  </FormLabel>
                  <Tooltip
                    label="O benefício municipal afeta o valor da alíquota. A alíquota padrão é 5%, aplicando o benefício ela irá para 2,5%"
                    placement="top"
                    hasArrow
                  >
                    <span>
                      <Icon
                        as={FaInfoCircle}
                        color="gray.400"
                        boxSize="0.875rem"
                        cursor="help"
                      />
                    </span>
                  </Tooltip>
                </HStack>
                <Checkbox
                  isChecked={formData.hasMunicipalBenefit}
                  onChange={(e) =>
                    handleFormChange('hasMunicipalBenefit', e.target.checked)
                  }
                >
                  <Text fontSize="sm">Aplicar benefício</Text>
                </Checkbox>
              </FormControl>
            </GridItem>
          </Grid>

          {/* Service Description */}
          <Grid templateColumns="repeat(12, 1fr)" gap="1rem">
            <GridItem colSpan={showComplementSelect ? 9 : 12}>
              <FormControl>
                <FormLabel fontSize="sm">Descrição do Serviço</FormLabel>
                <Input
                  size="sm"
                  value={formData.serviceDescription}
                  onChange={(e) =>
                    handleFormChange('serviceDescription', e.target.value)
                  }
                  placeholder="Descrição do serviço prestado"
                />
              </FormControl>
            </GridItem>

            {showComplementSelect && (
              <GridItem colSpan={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Complemento</FormLabel>
                  <Select
                    size="sm"
                    value={formData.serviceComplement}
                    onChange={(e) =>
                      handleFormChange('serviceComplement', e.target.value)
                    }
                    placeholder="Selecione..."
                  >
                    {SERVICE_COMPLEMENT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
            )}
          </Grid>

          {/* Summary Row */}
          {showPreview && (
            <Grid templateColumns="repeat(8, 1fr)" gap="1rem">
              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel fontSize="sm">Quantidade de Registros</FormLabel>
                  <Input
                    size="sm"
                    value={summary.quantity}
                    isReadOnly
                    bg="gray.50"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel fontSize="sm">Valor Total</FormLabel>
                  <Input
                    size="sm"
                    value={formatCurrency(summary.totalValue)}
                    isReadOnly
                    bg="gray.50"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel fontSize="sm">Alíquota</FormLabel>
                  <Input
                    size="sm"
                    value={formData.hasMunicipalBenefit ? '2,50%' : '5,00%'}
                    isReadOnly
                    bg="gray.50"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={3}>
                <HStack justify="flex-end" h="100%" align="end" gap="0.5rem">
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="yooga"
                    onClick={handleValidate}
                    isLoading={isValidating}
                    loadingText="Validando"
                  >
                    Validar
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="yooga"
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    loadingText="Criando"
                    isDisabled={!isValidated}
                    title={!isValidated ? 'Valide o lote antes de criar' : ''}
                  >
                    Criar Lote
                  </Button>
                </HStack>
              </GridItem>
            </Grid>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Stack gap="0.25rem" flex="1">
                <AlertDescription fontWeight="medium">
                  Nenhuma nota será emitida para esses documentos:
                </AlertDescription>
                {validationErrors.slice(0, 5).map((error, idx) => (
                  <Text key={idx} fontSize="sm">
                    {error}
                  </Text>
                ))}
                {validationErrors.length > 5 && (
                  <Text fontSize="sm" color="gray.500">
                    ... e mais {validationErrors.length - 5} erros
                  </Text>
                )}
              </Stack>
            </Alert>
          )}

          {/* CSV Upload Section */}
          {!showPreview && (
            <CsvUploader
              onValidated={handleCsvValidated}
              onClear={handleCsvClear}
              errors={csvErrors}
            />
          )}

          {/* Preview Table */}
          {showPreview && csvData.length > 0 && (
            <CsvPreview
              data={csvData}
              onClear={handleCsvClear}
              invalidDocuments={invalidDocuments}
            />
          )}

          {/* Loading */}
          {(isValidating || isSubmitting) && (
            <HStack justify="center" py="2rem">
              <Spinner color="yooga.500" />
              <Text>{isValidating ? 'Validando...' : 'Processando...'}</Text>
            </HStack>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default NfseNovoLote;
